import React, { useState, useEffect } from 'react';
import './ConoceColombiaPage.css';
import Header from './Header';
import AnimatedBackground from './AnimatedBackground';
import {
  ALEJA_EXPERIENCES,
  ALEJA_PROVIDER,
  TAPIRES_EXPERIENCES,
  TAPIRES_PROVIDER,
  PERSONALIZED_TRIP,
  PIPELINE_DESTINATIONS,
  formatCop,
  buildAlejaWhatsAppUrl,
  buildPersonalizedMailto,
  pipelineStatusLabel,
  pipelineStatusColor,
  COMMISSION_YEAR1_PCT
} from '../constants/conoceColombia';

const FALLBACK_IMAGE = '/images/hero-viajes-colombia.png';

const ConoceColombiaPage = () => {
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationTrip, setRegistrationTrip] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    comments: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openAlejaWhatsApp = (experience, e) => {
    e?.stopPropagation?.();
    window.open(buildAlejaWhatsAppUrl(experience), '_blank', 'noopener,noreferrer');
  };

  const openPersonalizedForm = (trip) => {
    setRegistrationTrip(trip);
    setSelectedExperience(null);
    setShowRegistration(true);
  };

  const handlePersonalizedSubmit = (e) => {
    e.preventDefault();
    window.location.href = buildPersonalizedMailto(form);
  };

  const renderExperienceCard = (item, { onPrimary, primaryLabel, badge } = {}) => (
    <article
      key={item.id}
      className={`trip-card ${item.isPersonalized ? 'trip-card-personalized' : ''} ${item.pipelineStatus ? 'trip-card-pipeline' : ''}`}
      onClick={() => {
        if (item.pipelineStatus) return;
        if (item.isPersonalized) openPersonalizedForm(item);
        else setSelectedExperience(item);
      }}
    >
      <div className="trip-image">
        <img src={item.image} alt={item.title} onError={(ev) => { ev.target.src = FALLBACK_IMAGE; }} />
        {badge && <span className="trip-package-badge">{badge}</span>}
        {item.slots && (
          <span className="trip-slots-badge">{item.slots} cupos</span>
        )}
        {item.pipelineStatus && (
          <span
            className="trip-pipeline-badge"
            style={{ backgroundColor: pipelineStatusColor(item.pipelineStatus) }}
          >
            {pipelineStatusLabel(item.pipelineStatus)}
          </span>
        )}
      </div>
      <div className="trip-content">
        {item.subtitle && <p className="trip-subtitle">{item.subtitle}</p>}
        <h3 className="trip-title">{item.title}</h3>
        {item.dates && (
          <p className="trip-dates">
            <i className="fas fa-calendar-alt" aria-hidden />
            {item.dates}
          </p>
        )}
        {item.datesNote && (
          <p className="trip-dates trip-dates--muted">
            <i className="fas fa-calendar-alt" aria-hidden />
            {item.datesNote}
          </p>
        )}
        <p className="trip-location">
          <i className="fas fa-map-marker-alt" aria-hidden />
          {item.location}
        </p>
        {item.priceCop != null && (
          <p className="trip-price">{formatCop(item.priceCop)}</p>
        )}
        {item.description && <p className="trip-description">{item.description}</p>}
        <ul className="trip-highlights">
          {(item.highlights || []).map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
        {!item.pipelineStatus && (
          <button
            type="button"
            className="trip-cta"
            onClick={(e) => {
              e.stopPropagation();
              if (onPrimary) onPrimary(item, e);
              else if (item.isPersonalized) openPersonalizedForm(item);
              else setSelectedExperience(item);
            }}
          >
            {primaryLabel || (item.isPersonalized ? 'Solicitar cotización' : 'Más información')}
            <i className="fas fa-arrow-right" aria-hidden />
          </button>
        )}
      </div>
    </article>
  );

  return (
    <div className="conoce-colombia-page">
      <Header />
      <AnimatedBackground particleCount={15} staticCount={4} />

      <section
        className="conoce-colombia-hero"
        style={{
          background: `linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.65) 100%), url(${process.env.PUBLIC_URL || ''}/images/hero-conoce-colombia-4destinos.png) center/cover no-repeat, url(${process.env.PUBLIC_URL || ''}/images/hero-viajes-colombia.png) center/cover no-repeat`,
          backgroundColor: '#0a0a0a'
        }}
      >
        <div className="cc-hero-copy-centered">
          <span className="cc-tag">JAVILAND</span>
          <h1 className="cc-headline">Conoce las tierras colombianas</h1>
          <p className="cc-narrative">
            Ecoturismo consciente con operadores aliados. Expediciones con fechas confirmadas,
            alianzas con RNT y proyectos propios en validación — sin perder lo que viene.
          </p>
          <p className="cc-subline">
            Referidos ecosistema · comisión año 1 {COMMISSION_YEAR1_PCT}% · ideal para viajeros internacionales
          </p>
          <button
            type="button"
            className="cc-cta"
            onClick={() => document.getElementById('cc-disponibles')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Ver experiencias
          </button>
        </div>
      </section>

      <main className="conoce-colombia-main">
        <div className="container">
          <section className="cc-section" id="cc-disponibles" aria-labelledby="cc-disponibles-title">
            <h2 id="cc-disponibles-title">Disponibles ahora</h2>
            <p className="cc-packages-intro">
              Reserva interés directo con el operador. Los leads pasan por Javiland para trazabilidad del ecosistema.
            </p>
            <div className="trips-grid">
              {ALEJA_EXPERIENCES.map((item) =>
                renderExperienceCard(item, {
                  badge: item.dates,
                  primaryLabel: 'Reservar por WhatsApp',
                  onPrimary: openAlejaWhatsApp
                })
              )}
              {renderExperienceCard(PERSONALIZED_TRIP, {
                badge: 'A tu medida',
                primaryLabel: 'Solicitar cotización'
              })}
            </div>
          </section>

          <section className="cc-section cc-section--alliance" id="cc-alianzas" aria-labelledby="cc-alianzas-title">
            <h2 id="cc-alianzas-title">Alianzas Conoce Colombia</h2>
            <p className="cc-packages-intro">
              Operadores del ecosistema Javiland. Comisión referido {COMMISSION_YEAR1_PCT}% año 1.
            </p>
            <div className="cc-alliance-providers">
              <article className="cc-provider-card">
                <h3>{ALEJA_PROVIDER.name}</h3>
                <p>{ALEJA_PROVIDER.specialty}</p>
                <div className="cc-provider-links">
                  <a href={ALEJA_PROVIDER.instagram} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram" aria-hidden /> {ALEJA_PROVIDER.instagramHandle}
                  </a>
                  <span>
                    <i className="fab fa-whatsapp" aria-hidden /> {ALEJA_PROVIDER.whatsappDisplay}
                  </span>
                </div>
              </article>
              <article className="cc-provider-card">
                <h3>{TAPIRES_PROVIDER.name}</h3>
                <p>{TAPIRES_PROVIDER.specialty} · {TAPIRES_PROVIDER.region}</p>
                <p className="cc-rnt-line">{TAPIRES_PROVIDER.operatorBadge}</p>
                <div className="cc-provider-links">
                  <a href={TAPIRES_PROVIDER.instagram} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram" aria-hidden /> {TAPIRES_PROVIDER.instagramHandle}
                  </a>
                  <span>{TAPIRES_PROVIDER.operatorName}</span>
                </div>
              </article>
            </div>

            {TAPIRES_EXPERIENCES.length > 0 && (
              <>
                <h3 className="cc-subsection-title">Expediciones Tapires</h3>
                <div className="trips-grid">
                  {TAPIRES_EXPERIENCES.map((item) =>
                    renderExperienceCard(item, {
                      badge: 'Tapires',
                      primaryLabel: 'Ver calendario',
                      onPrimary: () => {
                        window.open(item.calendarUrl, '_blank', 'noopener,noreferrer');
                      }
                    })
                  )}
                </div>
              </>
            )}
          </section>

          <section className="cc-section cc-section--pipeline" id="cc-pipeline" aria-labelledby="cc-pipeline-title">
            <h2 id="cc-pipeline-title">En el horizonte</h2>
            <p className="cc-packages-intro">
              Proyectos propios Javiland — visibles mientras cerramos operador, fechas o precio.
            </p>
            <div className="trips-grid trips-grid--pipeline">
              {PIPELINE_DESTINATIONS.map((item) => renderExperienceCard(item))}
            </div>
          </section>
        </div>
      </main>

      {selectedExperience && !selectedExperience.pipelineStatus && (
        <div className="trip-modal-overlay" onClick={() => setSelectedExperience(null)}>
          <div className="trip-modal trip-modal-plans" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="modal-close" onClick={() => setSelectedExperience(null)} aria-label="Cerrar">
              <i className="fas fa-times" aria-hidden />
            </button>
            <div className="trip-modal-content">
              <h2 className="trip-modal-title">{selectedExperience.title}</h2>
              {selectedExperience.subtitle && (
                <p className="trip-modal-subtitle">{selectedExperience.subtitle}</p>
              )}
              <div className="trip-modal-meta">
                {selectedExperience.dates && (
                  <span><i className="fas fa-calendar-alt" aria-hidden /> {selectedExperience.dates}</span>
                )}
                <span><i className="fas fa-map-marker-alt" aria-hidden /> {selectedExperience.location}</span>
                {selectedExperience.duration && (
                  <span><i className="fas fa-clock" aria-hidden /> {selectedExperience.duration}</span>
                )}
              </div>
              {selectedExperience.priceCop != null && (
                <p className="trip-modal-price">
                  {formatCop(selectedExperience.priceCop)}
                  {selectedExperience.depositCop != null && (
                    <span className="trip-modal-deposit">
                      {' '}· Depósito 30%: {formatCop(selectedExperience.depositCop)}
                    </span>
                  )}
                </p>
              )}
              {selectedExperience.slots && (
                <p className="trip-modal-slots"><strong>{selectedExperience.slots} cupos</strong></p>
              )}
              {selectedExperience.certifiedDiversOnly && (
                <p className="trip-modal-requirement">Solo buzos certificados</p>
              )}
              {selectedExperience.operatorLine && (
                <p className="trip-modal-operator">{selectedExperience.operatorLine}</p>
              )}
              {selectedExperience.includes && (
                <>
                  <h4 className="trip-modal-list-title">Incluye</h4>
                  <ul className="trip-modal-highlights">
                    {selectedExperience.includes.map((line, i) => (
                      <li key={i}><i className="fas fa-check" aria-hidden /> {line}</li>
                    ))}
                  </ul>
                </>
              )}
              {selectedExperience.excludes && (
                <>
                  <h4 className="trip-modal-list-title">No incluye</h4>
                  <ul className="trip-modal-excludes">
                    {selectedExperience.excludes.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </>
              )}
              {selectedExperience.optional && (
                <p className="trip-modal-optional"><em>Opcional:</em> {selectedExperience.optional}</p>
              )}
              <ul className="trip-modal-highlights">
                {(selectedExperience.highlights || []).map((h, i) => (
                  <li key={i}><i className="fas fa-check" aria-hidden /> {h}</li>
                ))}
              </ul>
              {selectedExperience.providerId === 'aleja' && (
                <button
                  type="button"
                  className="register-btn register-btn--wa"
                  onClick={() => openAlejaWhatsApp(selectedExperience)}
                >
                  <i className="fab fa-whatsapp" aria-hidden />
                  Reservar por WhatsApp (Aleja)
                </button>
              )}
              {selectedExperience.pdf && (
                <p className="trip-modal-pdf-note">
                  Ficha completa disponible en propuesta comercial Javiland.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {showRegistration && (
        <div
          className="registration-modal-overlay"
          onClick={() => { setShowRegistration(false); setRegistrationTrip(null); }}
        >
          <div className="registration-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button
              type="button"
              className="modal-close"
              onClick={() => { setShowRegistration(false); setRegistrationTrip(null); }}
              aria-label="Cerrar"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
            <div className="registration-content">
              <h3>
                {registrationTrip
                  ? `Cotización: ${registrationTrip.title}`
                  : 'Acompañamiento personalizado'}
              </h3>
              <p>
                Cuéntanos qué buscas. Al enviar se abrirá tu correo a ventas@javiland.com con los datos.
              </p>
              <form className="registration-form" onSubmit={handlePersonalizedSubmit}>
                <div className="form-group">
                  <label htmlFor="cc-name">Nombre completo</label>
                  <input
                    id="cc-name"
                    type="text"
                    required
                    placeholder="Ej. John Smith"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-email">Email</label>
                  <input
                    id="cc-email"
                    type="email"
                    required
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-phone">Teléfono (con código de país)</label>
                  <input
                    id="cc-phone"
                    type="tel"
                    required
                    placeholder="Ej. +1 555 123 4567"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-country">País de origen</label>
                  <input
                    id="cc-country"
                    type="text"
                    placeholder="Ej. Estados Unidos"
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-comments">Comentarios o preferencias</label>
                  <textarea
                    id="cc-comments"
                    rows="3"
                    placeholder="Fechas tentativas, número de viajeros, presupuesto..."
                    value={form.comments}
                    onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                  />
                </div>
                <button type="submit" className="pay-btn">
                  <i className="fas fa-paper-plane" aria-hidden />
                  Enviar solicitud
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConoceColombiaPage;
